import type { MFE } from "@sinch/mfe-types";
import { importRemote } from "module-federation-import-remote";
import type { ENV } from "../helpers/getEnv";
import {
  MFE_OVERRIDE_KEY,
  MFE_VARIANT_OVERRIDE_KEY,
  getBaseMfeConfig,
  getMfeConfig,
  type RemoteName,
} from "./mfeConfig";

// set in the template
const MFE_TEST_KEY = "_mfeMock";

const insertScriptPreloadLink = (url: string) => {
  const el = document.createElement("link");
  el.rel = "preconnect";
  el.href = url;
  el.crossOrigin = "anonymous";
  document.head.append(el);
};

const storeOverridesFromHash = () => {
  const params = new URLSearchParams(location.hash.slice(1));
  const overrides = params.getAll(MFE_OVERRIDE_KEY);

  if (overrides.length) {
    overrides.length &&
      sessionStorage.setItem(MFE_OVERRIDE_KEY, JSON.stringify(overrides));
    params.delete(MFE_OVERRIDE_KEY);
    const newHash = params.toString();
    const formattedHash = newHash ? "#" + newHash : "";
    const newLocation = location.pathname + location.search + formattedHash;
    history.replaceState(history.state, "", newLocation);
  }
  if (sessionStorage.getItem(MFE_OVERRIDE_KEY))
    document.body.classList.add("override-indicator");
};

const handleVariantHashParam = () => {
  const params = new URLSearchParams(location.hash.slice(1));
  const variantOverride = params.getAll(MFE_VARIANT_OVERRIDE_KEY);

  if (variantOverride.length) {
    variantOverride.length &&
      sessionStorage.setItem(
        MFE_VARIANT_OVERRIDE_KEY,
        JSON.stringify(variantOverride)
      );
    params.delete(MFE_VARIANT_OVERRIDE_KEY);
    const newHash = params.toString();
    const formattedHash = newHash ? "#" + newHash : "";
    const newLocation = location.pathname + location.search + formattedHash;
    history.replaceState(history.state, "", newLocation);
  }
  if (sessionStorage.getItem(MFE_VARIANT_OVERRIDE_KEY))
    document.body.classList.add("override-indicator");
};

const handleTestHashParam = () => {
  const params = new URLSearchParams(location.hash.slice(1));
  if (!params.has(MFE_TEST_KEY)) {
    return false;
  }

  const overrides = Object.keys(getBaseMfeConfig())
    .filter((key) => key !== "EngageUI") // Do not mock Engage ui as this is where the menu now lives
    .map((key) => `${key}@/testMFEEntry.js?container=${key}`);

  if (!overrides.length) {
    return false;
  }
  sessionStorage.setItem(MFE_OVERRIDE_KEY, JSON.stringify(overrides));
  params.delete(MFE_TEST_KEY);
  const newHash = params.toString();
  const formattedHash = newHash ? "#" + newHash : "";
  const newLocation = location.pathname + location.search + formattedHash;
  history.replaceState(history.state, "", newLocation);
  return true;
};

const preloadMFE = () => {
  Object.values(getMfeConfig())
    .map((url) => url + "/remoteEntry.js")
    .forEach(insertScriptPreloadLink);
};

export const setupMFELoaders = (env: typeof ENV) => {
  const hasTestParam = handleTestHashParam();
  if (!hasTestParam) {
    handleVariantHashParam();
    if (env !== "prod") {
      storeOverridesFromHash();
    }
  }
  preloadMFE();
};

export type MFELoader = {
  default: MFE;
};

const remoteEntryFileMatch = new RegExp(/\/(?!.*\/)(.*\.js.+)/);
const testFileMatch = new RegExp(/testMFEEntry.js/);

export const loadMFE = async (
  scope: RemoteName,
  module: string
): Promise<MFE> => {
  const mfe = (await getMfeConfig())[scope];

  if (!mfe) {
    throw new Error("No Config for that MFE: " + scope);
  }

  if (mfe.flags?.includes("esm")) {
    return import(/* webpackIgnore: true */ mfe.url + "/remoteEntry.js").then(
      (x) => x.default
    );
  }

  const remoteEntryFileName =
    remoteEntryFileMatch.exec(mfe.url)?.[1] || "remoteEntry.js";

  const mfeLoader = await importRemote<MFELoader>({
    url: testFileMatch.test(remoteEntryFileName)
      ? ""
      : mfe.url.replace("/" + remoteEntryFileName, ""),
    scope,
    module,
    remoteEntryFileName,
    bustRemoteEntryCache: false,
  });

  return mfeLoader.default;
};
